// Volts kontrolløkke (DESIGN.md: Kontrolløkke). Kører i browseren, drevet af update() hvert billede.
//
//   hver ~3 s:  kandidater (top 7 + vent) -> Jev (deadline ~0,85 s) -> valg + confidence
//               >= 0,8: annoncér, handl efter 1,2 s
//               0,6-0,8: annoncér, handl efter 2 s
//               < 0,6: tøv synligt, spørg spilleren med top 2, tag toppen efter 8 s
//               timeout eller fejl: rygmarven (reservebotten) vælger, uden at spilleren mærker det
//   hver 45-60 s og ved store hændelser: Haiku lægger en plan, der vægter rangeringen
//   ved hændelser: Haiku siger en replik (højst hver 12. s)
//
// Tempo: højst én handling pr. 1,7 s. "Jeg tager den" låser målet i 20 s. Kandidater nær
// spillerens cursor de sidste 2 s fjernes, også lige før handlingen udføres. Ingen DOM her.

import { check } from '../sim/actions';
import { hzZone } from '../sim/hz';
import { roleBlocked } from '../sim/roles';
import { SEASON_TICKS } from '../sim/types';
import type { Candidate, CardOption, PlayerId, State } from '../sim/types';
import { buildCandidates, buildWireState, cardCandidates, labelOf, needsYes } from './candidates';
import type { VoltCandidate } from './candidates';
import { bottleneck, cityMoves, moveKey, truthConflict } from './truth';
import type { CityMove } from './truth';
import { deadlineFor, hash, isFallback, policyFromText, questionReply } from './client';
import type { Transport } from './client';
import { DEFAULT_POLICY, LIMITS, clipLine, planUnder, sanePolicy } from './protocol';
import type { AgentAnswer, FallbackReason, LineEvent, Mood, Plan, Policy } from './protocol';

/** Glade replikker, der ikke passer, mens en by er i nød. */
const CHEER: ReadonlySet<LineEvent> = new Set<LineEvent>(['built', 'upgraded', 'player_built', 'year', 'idle']);

/** Er en aktiv by mørk, eller løber dens mørkeur? */
function troubled(s: State): boolean {
  return s.nodes.some((n) => n.active && n.city && (n.city.out || n.city.darkTicks > 0 || n.city.clock > 0));
}

export const TEMPO = {
  /** Mellem to tænkerunder. */
  thinkMs: 3000,
  /** Mindst så lang tid mellem to handlinger. */
  minGapMs: 1700,
  sureMs: 1200,
  maybeMs: 2000,
  askMs: 8000,
  sure: 0.8,
  maybe: 0.6,
  claimMs: 20_000,
  firstPlanMs: 5000,
  planMs: 45_000,
  planJitterMs: 15_000,
  /** Mindst så længe mellem to planer, også ved store hændelser. */
  planMinMs: 15_000,
  lineGapMs: 12_000,
  /** Spørg højst så tit; ellers handler Volt efter 2 s som ved "måske". */
  askGapMs: 15_000,
  /** Første træk venter på hilsenen, men højst så længe efter banens start. */
  greetMs: 3000,
  /** Reservebottens score skal over dette, før rygmarven handler. */
  spineMin: 5,
  /** "Spar penge" gælder et halvt år (to årstider) i spiltid, så glemmer Volt det og siger det højt. */
  saveTicks: SEASON_TICKS * 2,
  /** Højst én forklaring pr. så lang tid, når et træk ikke blev til noget. */
  missGapMs: 10_000,
  /** Spørg højst så tit i spiltid (ticks): 36 s, så et spil på pause eller i fart ikke ændrer det. Det
   *  første spørgsmål tidligst efter det halve (spilleren skal lige se banen først). */
  askGapTicks: 360,
  /** Tidligste tick for Volts første spørgsmål i en bane. */
  firstAskTick: 120,
  /** Det samme spørgsmål kommer tidligst igen efter så lang spiltid (ticks): 60 s. */
  repeatTicks: 600,
  /** "Det er dit træk" om den samme by højst så tit (ticks), og om nogen by højst hvert 15. s. */
  nudgeCityTicks: 300,
  nudgeTicks: 150,
  /** Frekvensnålen: højst ét tryk pr. så mange ms (controller.chore). */
  choreMs: 1500,
} as const;

/** "Viborg: Skive-Viborg er fuld. Opgradér den, det er dit træk." Højst 12 ord (clipLine). */
export function nudgeLine(s: State, m: CityMove): string {
  const b = bottleneck(s, m.city.id);
  if (m.move.kind === 'upgrade_line' && b.kind === 'full') return `${m.city.name}: ${b.a}-${b.b} er fuld. Opgradér den, det er dit træk.`;
  const cand: Candidate = { id: '', kind: m.move.kind, cost: m.cost };
  if (m.move.target !== undefined) cand.target = m.move.target;
  const what = m.move.kind === 'build_line' ? labelOf(s, cand).replace(/^Byg (luftledning|kabel|søkabel|ledning) /, 'Træk ') : labelOf(s, cand);
  return `${m.city.name} mangler strøm. ${what}, det er dit træk.`;
}

/** Spørgsmålets nøgle: det første valg (art og mål). */
export function askKey(top: readonly VoltCandidate[]): string {
  const c = top[0];
  return c ? moveKey(c.cand.kind, c.cand.target) : '';
}

/** Hvorfor et træk ikke gik, sagt kort af Volt. */
const MISS: Record<string, string> = {
  too_poor: 'Kassen rakte ikke. Jeg finder noget billigere.',
  already_exists: 'Den nåede du før mig. Fint, jeg tager en anden.',
  duplicate: 'Den nåede du før mig. Fint, jeg tager en anden.',
  no_slots: 'Stationen var fuld. Jeg finder et andet sted.',
  busy: 'Holdet var optaget. Jeg prøver noget andet.',
  no_crew: 'Holdet var optaget. Jeg prøver noget andet.',
  skipped: 'Du er i gang dér. Jeg finder noget andet.',
};
const MISS_ANY = 'Det træk gik ikke. Jeg finder et andet.';

export type Brain = 'jev' | 'rygmarv' | 'player';

export interface ThoughtCand {
  id: string;
  label: string;
  score: number;
  /** Jevs sandsynlighed (0..1), eller null når rygmarven valgte. */
  p: number | null;
}

export interface Decision {
  at: number;
  cands: ThoughtCand[];
  choice: string;
  label: string;
  by: Brain;
  /** Svartid i ms (Jev), eller null. */
  ms: number | null;
  confidence: number | null;
  reason?: FallbackReason;
  outcome: 'pending' | 'done' | 'wait' | 'asked' | 'skipped' | 'claimed' | 'failed';
}

export interface VoltUi {
  thinking(on: boolean): void;
  /** Tankepanelet: seneste beslutning med kandidater og sandsynligheder. */
  thought(d: Decision): void;
  /** Volt siger, hvad han vil gøre, og gør det om `inMs`. */
  announce(c: VoltCandidate, inMs: number, by: Brain): void;
  /** Volt er i tvivl: vis top 2 som knapper. */
  /** `proposal`: et anlæg Volt kun bygger, hvis spilleren siger ja. */
  ask(top: VoltCandidate[], timeoutMs: number, proposal?: boolean): void;
  clearAsk(): void;
  say(line: string, mood: Mood): void;
  /** Hvem der svarer lige nu: Jev med svartid, eller rygmarven. */
  brain(by: 'jev' | 'rygmarv', ms: number | null): void;
  acted(c: VoltCandidate, by: Brain): void;
  /** Den annoncerede handling er droppet (ny instruks, vent på spilleren): fjern "Mit træk". */
  dropped(): void;
  /** Forslag til et hændelseskort. */
  suggest(threatId: string, opt: CardOption | null, label: string, by: 'jev' | 'rygmarv'): void;
  /** Spillerens aktive ønske ændrede sig (vises som mærker, der kan fjernes). */
  policy(p: Policy): void;
}

export interface ControllerDeps {
  transport: Transport;
  /** Hvem Volt spiller som (standard p2; i solo med roller den rolle, spilleren ikke valgte). */
  player?: PlayerId;
  now(): number;
  state(): State;
  /** Må Volt handle lige nu (spillet kører, ingen skærm foran)? */
  canAct(): boolean;
  /** Læg handlingen i køen som p2. Sand, hvis den gik igennem tjekket; ellers falsk eller fejlkoden. */
  push(c: Candidate, by: 'jev' | 'bot'): boolean | string;
  nearCursor?(nodes: readonly string[]): boolean;
  /**
   * En replik, når Volt skal holde sig væk lige nu (spilleren lærer at trække sin første ledning),
   * ellers null. Mens den står, tænker og handler Volt ikke; replikken siges én gang.
   */
  hold?(): string | null;
  /** Mål, vejledningen peger spilleren hen til (node- og lednings-id'er): dem tager Volt ikke. */
  reserved?(): readonly string[];
  ui?: Partial<VoltUi>;
}

export interface VoltStats {
  decisions: number;
  actions: number;
  asks: number;
  /** Median af Jevs svartider (ms), eller null. */
  medianMs: number | null;
  p90Ms: number | null;
  jev: number;
  spine: number;
}

type Phase =
  | { k: 'idle' }
  | { k: 'waiting'; req: number; sentAt: number; deadline: number; cands: VoltCandidate[] }
  | { k: 'pending'; at: number; cand: VoltCandidate; by: Brain; d: Decision }
  | { k: 'asking'; until: number; top: VoltCandidate[]; d: Decision; proposal?: boolean };

export function quantile(xs: readonly number[], q: number): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(q * s.length))] as number;
}

/** Median som i logbogen: midterværdien (gennemsnit af de to midterste ved lige antal). */
export function median(xs: readonly number[]): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? (s[m] as number) : ((s[m - 1] as number) + (s[m] as number)) / 2;
}

export class VoltController {
  policy: Policy = { ...DEFAULT_POLICY, avoid: [] };
  plan: Plan | null = null;
  readonly log: Decision[] = [];
  private phase: Phase = { k: 'idle' };
  private reqSeq = 0;
  private nextThink = 0;
  private lastActAt = -Infinity;
  private lastAskAt = -Infinity;
  private slow = false;
  private recent: string[] = [];
  private claims = new Map<string, number>();
  private nextPlanAt = 0;
  private lastPlanAt = -Infinity;
  private planCount = 0;
  private planInFlight = false;
  private lastLineAt = -Infinity;
  private lineInFlight = false;
  private cardReq = new Map<string, number>();
  private latencies: number[] = [];
  private counts = { decisions: 0, actions: 0, asks: 0, jev: 0, spine: 0 };
  private started = false;
  private greetUntil = -Infinity;
  private held = false;
  private lastMissAt = -Infinity;
  /** Spiltid (tick), hvor "spar" blev sagt; null når Volt ikke sparer på spillerens ord. */
  private saveSince: number | null = null;
  /** Spiltid for sidste spørgsmål og dets nøgle (spor "sandhed": højst ét pr. 30 s, aldrig det samme i træk). */
  private lastAskTick = -Infinity;
  private lastAskKey = '';
  /** "Det er dit træk": sidste gang pr. by og i alt (ticks). */
  private nudged = new Map<string, number>();
  private lastNudgeTick = -Infinity;
  /** Et forslag, der udløb uden svar: det får en ny chance, men tidligst efter repeatTicks. */
  private expired: string | null = null;
  private readonly ui: Partial<VoltUi>;

  constructor(private readonly deps: ControllerDeps) {
    this.ui = deps.ui ?? {};
  }

  /** Ny bane: nulstil alt undtagen spillerens instruks. */
  reset(): void {
    this.phase = { k: 'idle' };
    this.reqSeq++;
    const t = this.deps.now();
    this.nextThink = t + 1500;
    this.lastActAt = -Infinity;
    this.lastAskAt = -Infinity;
    this.recent = [];
    this.claims.clear();
    this.plan = null;
    this.nextPlanAt = t + TEMPO.firstPlanMs;
    this.lastPlanAt = -Infinity;
    this.planCount = 0;
    this.lastLineAt = -Infinity;
    this.cardReq.clear();
    this.latencies = [];
    this.counts = { decisions: 0, actions: 0, asks: 0, jev: 0, spine: 0 };
    this.log.length = 0;
    this.started = false;
    this.greetUntil = -Infinity;
    this.held = false;
    this.lastMissAt = -Infinity;
    this.lastAskTick = -Infinity;
    this.lastAskKey = '';
    this.nudged.clear();
    this.lastNudgeTick = -Infinity;
    this.expired = null;
    this.saveSince = this.policy.priority === 'money' ? 0 : null;
    this.ui.clearAsk?.();
    this.ui.thinking?.(false);
  }

  get busy(): Phase['k'] {
    return this.phase.k;
  }

  /** Det Volt er ved at gøre eller spørger om (til markøren på kortet). */
  get intent(): VoltCandidate | null {
    if (this.phase.k === 'pending') return this.phase.cand;
    if (this.phase.k === 'asking') return this.phase.top[0] ?? null;
    return null;
  }

  stats(): VoltStats {
    return {
      ...this.counts,
      medianMs: median(this.latencies),
      p90Ms: quantile(this.latencies, 0.9),
    };
  }

  // -------------------------------------------------------------------------
  // Hovedløkken

  update(): void {
    const now = this.deps.now();
    for (const [k, until] of this.claims) if (until <= now) this.claims.delete(k);
    const hold = this.deps.hold?.() ?? null;
    if (!this.started && this.deps.canAct()) {
      this.started = true;
      // Første spørgsmål tidligst 12 s inde i banen: spilleren skal lige se kortet først.
      this.lastAskTick = TEMPO.firstAskTick - TEMPO.askGapTicks;
      // Venter Volt på spilleren, er det hilsenen; ellers venter første træk på Haikus hilsen.
      if (hold) this.sayOnce(hold, 'calm');
      else {
        this.line('start');
        this.greetUntil = now + TEMPO.greetMs;
      }
    }
    // Nålen er en refleks, ikke et træk: Volt holder den også, mens han venter på spillerens første
    // ledning (ellers kostede ventetiden penge, spillerens egen playtest som Planlægger).
    this.chore(now);
    if (hold) {
      if (this.phase.k !== 'idle') this.drop();
      this.held = true;
      return;
    }
    this.expireSave();
    if (this.held) {
      // Spilleren har lige gjort sit: giv et øjeblik, før Volt går i gang.
      this.held = false;
      this.nextThink = Math.max(this.nextThink, now + 1500);
    }
    this.revalidate(now);
    this.nudge();
    const p = this.phase;
    if (p.k === 'waiting' && now >= p.deadline + 50) {
      // Vagten: transporten skulle selv have givet op, men svaret kom ikke. Rygmarven tager over.
      this.spine(p.cands, 'timeout', null);
    } else if (p.k === 'pending' && now >= p.at) {
      this.execute(p.cand, p.by, p.d);
    } else if (p.k === 'asking' && now >= p.until) {
      this.ui.clearAsk?.();
      const top = p.top[0];
      // Et forslag uden svar er et nej lige nu: Volt bygger ikke for spilleren. Men det udløber ikke
      // stille (runde 5): han siger det, og forslaget får en ny chance senere.
      if (p.proposal) {
        this.expired = askKey(p.top);
        this.say('Jeg lader den ligge og spørger igen senere.', 'calm');
        this.skip(p.d);
      }
      else if (top) this.schedule(top, now, 'jev', p.d);
      else this.phase = { k: 'idle' };
    } else if (p.k === 'idle' && now >= this.nextThink && this.deps.canAct() && !(this.lineInFlight && now < this.greetUntil)) {
      this.think();
    }
    if (now >= this.nextPlanAt && !this.planInFlight && this.deps.canAct()) this.askPlan();
  }

  private lastChoreAt = -Infinity;

  /**
   * Spor: tillid. Er Volt Driftsvagt, holder han frekvensnålen (sim/hz.ts) i det grønne som en refleks
   * ved siden af tænkningen: ingen tænkerunde, ingen annonce. Han trykker først, når nålen er ude af
   * båndet, så spilleren kan se den glide og ham ordne den, og højst ét tryk pr. TEMPO.choreMs.
   */
  private chore(now: number): void {
    const s = this.deps.state();
    if (!s.roles || !s.hz || roleBlocked(s, 'trim_hz', this.me)) return;
    if (now - this.lastChoreAt < TEMPO.choreMs || !this.deps.canAct()) return;
    if (hzZone(s.hz.dev) === 'green' || !check(s, 'trim_hz', undefined, undefined, this.me).ok) return;
    this.lastChoreAt = now;
    this.deps.push({ id: 'trim_hz::', kind: 'trim_hz', cost: 0 }, 'bot');
  }

  private think(): void {
    const now = this.deps.now();
    this.nextThink = now + TEMPO.thinkMs * (this.slow ? 2 : 1);
    const state = this.deps.state();
    const cands = buildCandidates(state, {
      player: this.me,
      policy: this.policy,
      plan: this.plan,
      recent: new Set(this.recent),
      locked: new Set([...this.claims.keys(), ...(this.deps.reserved?.() ?? [])]),
      nearCursor: this.deps.nearCursor ? (n) => this.deps.nearCursor!(n) : undefined,
    });
    // Kun "vent" tilbage: ingen grund til at spørge nogen.
    if (cands.length < 2) return;
    const req = ++this.reqSeq;
    const deadline = deadlineFor('hands');
    this.phase = { k: 'waiting', req, sentAt: now, deadline: now + deadline, cands };
    this.ui.thinking?.(true);
    const wire = buildWireState(state, { plan: this.teamPlan, policy: this.policy, recent: this.recent, player: this.me });
    this.deps.transport
      .call({ kind: 'hands', state: wire, candidates: cands.map((c) => c.wire) }, deadline)
      .then((a) => this.onAct(req, a))
      .catch(() => this.onAct(req, { fallback: true, reason: 'error' }));
  }

  private onAct(req: number, a: AgentAnswer): void {
    const p = this.phase;
    // Sene svar (efter vagten, en ny bane eller "Jeg tager den") ignoreres.
    if (p.k !== 'waiting' || p.req !== req) return;
    const now = this.deps.now();
    this.slow = !isFallback(a) && a.slow === true;
    if (isFallback(a) || !('choice' in a)) {
      this.spine(p.cands, isFallback(a) ? a.reason : 'error', isFallback(a) ? (a.line ?? null) : null);
      return;
    }
    const ms = Math.round(now - p.sentAt);
    const cand = p.cands.find((c) => c.cand.id === a.choice);
    if (!cand) {
      this.spine(p.cands, 'invalid', null);
      return;
    }
    this.latencies.push(ms);
    this.counts.jev++;
    this.counts.decisions++;
    this.ui.thinking?.(false);
    this.ui.brain?.('jev', ms);
    const d = this.decision(p.cands, cand, 'jev', ms, a.confidence, a.probabilities);
    if (cand.cand.kind === 'wait') {
      d.outcome = 'wait';
      this.phase = { k: 'idle' };
      this.ui.thought?.(d);
      return;
    }
    if (needsYes(cand, this.policy)) {
      const byP = (x: VoltCandidate, y: VoltCandidate) => (a.probabilities[y.cand.id] ?? 0) - (a.probabilities[x.cand.id] ?? 0) || y.score - x.score;
      this.propose(p.cands, cand, d, byP);
      return;
    }
    const top = [...p.cands]
      .filter((c) => c.cand.kind !== 'wait')
      .sort((x, y) => (a.probabilities[y.cand.id] ?? 0) - (a.probabilities[x.cand.id] ?? 0) || y.score - x.score)
      .slice(0, 2);
    if (a.confidence >= TEMPO.sure) this.schedule(cand, now + TEMPO.sureMs, 'jev', d);
    // "Byg løs" betyder, at Volt selv handler inden for sin rolle: han spørger ikke, når han er i tvivl.
    else if (a.confidence >= TEMPO.maybe || this.policy.avoid.includes('wait') || !this.mayAsk(top)) this.schedule(cand, now + TEMPO.maybeMs, 'jev', d);
    else {
      if (top.length === 0) {
        this.phase = { k: 'idle' };
        return;
      }
      d.outcome = 'asked';
      this.asked(top);
      this.phase = { k: 'asking', until: now + TEMPO.askMs, top, d };
      this.ui.thought?.(d);
      this.ui.ask?.(top, TEMPO.askMs);
    }
  }

  /** Rygmarven: reservebottens bedste bud, hvis det er godt nok. */
  private spine(cands: VoltCandidate[], reason: FallbackReason, line: string | null): void {
    const now = this.deps.now();
    this.ui.thinking?.(false);
    this.ui.brain?.('rygmarv', null);
    if (line) this.sayOnce(line, 'calm');
    const best = [...cands].filter((c) => c.cand.kind !== 'wait').sort((a, b) => b.score - a.score)[0];
    this.counts.spine++;
    if (!best || best.score <= TEMPO.spineMin) {
      this.phase = { k: 'idle' };
      return;
    }
    this.counts.decisions++;
    const d = this.decision(cands, best, 'rygmarv', null, null, null);
    d.reason = reason;
    if (needsYes(best, this.policy)) {
      this.propose(cands, best, d, (x, y) => y.score - x.score);
      return;
    }
    this.schedule(best, now + TEMPO.sureMs, 'rygmarv', d);
  }

  /**
   * Et forslag til spilleren: højst to anlæg, "Ikke nu" eller intet svar er et nej. Højst ét
   * forslag pr. askGapMs, så Volt ikke maser; imens venter han.
   */
  private propose(cands: VoltCandidate[], choice: VoltCandidate, d: Decision, order: (x: VoltCandidate, y: VoltCandidate) => number): void {
    const now = this.deps.now();
    const rest = cands.filter((c) => c !== choice && needsYes(c, this.policy)).sort(order);
    // Det samme spørgsmål kommer ikke to gange i træk: så spørger Volt om det næstbedste.
    let all = [choice, ...rest];
    if (this.repeats(all)) all = rest;
    const top = all.slice(0, 2);
    if (top.length === 0 || !this.mayAsk(top)) {
      d.outcome = 'wait';
      this.phase = { k: 'idle' };
      this.ui.thought?.(d);
      return;
    }
    if (top[0] !== choice) {
      d.choice = top[0]!.cand.id;
      d.label = top[0]!.label;
    }
    d.outcome = 'asked';
    this.asked(top);
    this.phase = { k: 'asking', until: now + TEMPO.askMs, top, d, proposal: true };
    this.ui.thought?.(d);
    this.ui.ask?.(top, TEMPO.askMs, true);
  }

  /** Hvem Volt spiller som. */
  private get me(): PlayerId {
    return this.deps.player ?? 'p2';
  }

  /** Ville spørgsmålet gentage det forrige inden for repeatTicks? */
  private repeats(top: readonly VoltCandidate[]): boolean {
    const tick = this.deps.state().tick;
    return askKey(top) === this.lastAskKey && tick - this.lastAskTick < TEMPO.repeatTicks;
  }

  /** Må Volt spørge nu: 36 s spiltid (og 15 s rigtig tid) siden sidst, og ikke det samme igen? */
  private mayAsk(top: readonly VoltCandidate[]): boolean {
    const now = this.deps.now();
    const tick = this.deps.state().tick;
    if (now - this.lastAskAt < TEMPO.askGapMs || tick - this.lastAskTick < TEMPO.askGapTicks) return false;
    return top.length > 0 && !this.repeats(top);
  }

  private asked(top: readonly VoltCandidate[]): void {
    this.counts.asks++;
    this.lastAskAt = this.deps.now();
    this.lastAskTick = this.deps.state().tick;
    this.lastAskKey = askKey(top);
    if (this.lastAskKey === this.expired) this.expired = null;
  }

  /** Er Volts træk forældet: kan ikke gøres, er makkerens, eller strider mod byens træk? */
  stale(c: VoltCandidate): boolean {
    const s = this.deps.state();
    if (s.phase !== 'playing' || c.cand.kind === 'wait') return false;
    if (roleBlocked(s, c.cand.kind, this.me)) return true;
    const r = check(s, c.cand.kind, c.cand.target, c.cand.opt, this.me);
    if (!r.ok && r.error !== 'too_poor') return true;
    return truthConflict(s, c.cand, c.nodes) !== null;
  }

  /** Hvert billede: et annonceret træk eller et spørgsmål, der ikke længere passer, fjernes med det samme. */
  private revalidate(now: number): void {
    const p = this.phase;
    const bad = p.k === 'pending' ? this.stale(p.cand) : p.k === 'asking' ? p.top.some((c) => this.stale(c)) : false;
    if (!bad) return;
    this.drop();
    this.nextThink = Math.min(this.nextThink, now + 800);
  }

  /**
   * En by i nød, hvis træk er spillerens: Volt siger det, så spilleren ved, at det er deres tur (runde 5:
   * "3 byer i nød, og Volt sagde intet"). Højst én gang pr. 30 s pr. by og hvert 15. s i alt. Ikke et spørgsmål.
   */
  private nudge(): void {
    const s = this.deps.state();
    if (!s.roles || s.phase !== 'playing' || s.tick % 10 !== 0 || s.tick - this.lastNudgeTick < TEMPO.nudgeTicks) return;
    for (const m of cityMoves(s)) {
      if (m.owner === null || m.owner === this.me || !m.ok) continue;
      const c = m.city.city;
      if (!c || (c.clock <= 0 && !c.out)) continue;
      if (s.tick - (this.nudged.get(m.city.id) ?? -Infinity) < TEMPO.nudgeCityTicks) continue;
      this.nudged.set(m.city.id, s.tick);
      this.lastNudgeTick = s.tick;
      this.say(nudgeLine(s, m), 'worried');
      return;
    }
  }

  private skip(d: Decision): void {
    d.outcome = 'skipped';
    this.ui.thought?.(d);
    this.phase = { k: 'idle' };
    this.nextThink = this.deps.now() + TEMPO.thinkMs;
  }

  private decision(
    cands: VoltCandidate[],
    choice: VoltCandidate,
    by: Brain,
    ms: number | null,
    confidence: number | null,
    probs: Record<string, number> | null,
  ): Decision {
    const d: Decision = {
      at: this.deps.now(),
      cands: cands.map((c) => ({ id: c.cand.id, label: c.label, score: Math.round(c.score), p: probs ? (probs[c.cand.id] ?? 0) : null })),
      choice: choice.cand.id,
      label: choice.label,
      by,
      ms,
      confidence,
      outcome: 'pending',
    };
    this.log.push(d);
    if (this.log.length > 30) this.log.shift();
    return d;
  }

  private schedule(cand: VoltCandidate, at: number, by: Brain, d: Decision): void {
    // Tempo: aldrig to handlinger tættere end minGap.
    const when = Math.max(at, this.lastActAt + TEMPO.minGapMs);
    this.phase = { k: 'pending', at: when, cand, by, d };
    this.ui.thought?.(d);
    this.ui.announce?.(cand, Math.max(0, when - this.deps.now()), by);
  }

  private execute(cand: VoltCandidate, by: Brain, d: Decision): void {
    const now = this.deps.now();
    if (!this.deps.canAct()) {
      // Pause eller en skærm foran: vent, til spillet kører igen.
      this.phase = { k: 'pending', at: now + 250, cand, by, d };
      return;
    }
    this.phase = { k: 'idle' };
    const c = cand.cand;
    const target = c.target ?? '';
    const blocked =
      this.claims.has(target) ||
      cand.nodes.some((n) => this.claims.has(n)) ||
      (cand.nodes.length > 0 && this.deps.nearCursor?.(cand.nodes) === true);
    if (blocked || this.stale(cand)) {
      d.outcome = 'skipped';
      this.ui.thought?.(d);
      this.ui.clearAsk?.();
      this.ui.dropped?.();
      this.miss('skipped', now);
      this.nextThink = Math.min(this.nextThink, now + 800);
      return;
    }
    const res = this.deps.push(c, by === 'rygmarv' ? 'bot' : 'jev');
    const ok = res === true;
    d.outcome = ok ? 'done' : 'failed';
    this.ui.thought?.(d);
    if (!ok) {
      // "Mit træk" må ikke blive stående, når trækket ikke blev til noget: fjern det og sig hvorfor.
      this.ui.dropped?.();
      if (res !== 'paused') this.miss(typeof res === 'string' ? res : '', now);
      this.nextThink = Math.min(this.nextThink, now + 800);
      return;
    }
    this.lastActAt = now;
    this.nextThink = Math.max(this.nextThink, now + 800);
    this.counts.actions++;
    this.recent.push(c.id);
    if (this.recent.length > LIMITS.recent) this.recent.shift();
    this.ui.acted?.(cand, by);
  }

  // -------------------------------------------------------------------------
  // Spilleren

  /** Spilleren valgte en af de to knapper. */
  answer(id: string): void {
    const p = this.phase;
    if (p.k !== 'asking') return;
    const c = p.top.find((x) => x.cand.id === id);
    this.ui.clearAsk?.();
    // "Ikke nu" (eller et ukendt svar): Volt lader det ligge.
    if (!c) return this.skip(p.d);
    p.d.by = 'player';
    p.d.choice = c.cand.id;
    p.d.label = c.label;
    this.schedule(c, this.deps.now(), 'player', p.d);
  }

  /** "Jeg tager den": drop Volts igangværende handling og lad målet være i 20 s. */
  claim(target?: string): void {
    const now = this.deps.now();
    const p = this.phase;
    const lock = (k: string) => k && this.claims.set(k, now + TEMPO.claimMs);
    if (target) lock(target);
    if (p.k === 'pending' || p.k === 'asking') {
      const cs = p.k === 'pending' ? [p.cand] : p.top;
      for (const c of cs) {
        lock(c.cand.target ?? '');
        for (const n of c.nodes) lock(n);
      }
      p.d.outcome = 'claimed';
      this.ui.thought?.(p.d);
      this.ui.clearAsk?.();
      this.phase = { k: 'idle' };
      this.nextThink = now + TEMPO.thinkMs;
    } else if (p.k === 'waiting') {
      this.phase = { k: 'idle' };
      this.ui.thinking?.(false);
    }
  }

  /** Glem en annonceret handling, et spørgsmål eller et svar på vej (et sent svar ignoreres i onAct). */
  private drop(): void {
    const p = this.phase;
    if (p.k === 'pending' || p.k === 'asking') {
      p.d.outcome = 'skipped';
      this.ui.thought?.(p.d);
    }
    this.ui.clearAsk?.();
    this.ui.dropped?.();
    this.ui.thinking?.(false);
    this.phase = { k: 'idle' };
  }

  /** Kort forklaring, når et træk ikke gik, højst hvert 10. s. */
  private miss(why: string, now: number): void {
    if (now - this.lastMissAt < TEMPO.missGapMs) return;
    this.lastMissAt = now;
    this.say(MISS[why] ?? MISS_ANY, 'calm');
  }

  /** Planen, som Jev og vægtningen ser: Haikus plan under spillerens ønske. */
  get teamPlan(): Plan | null {
    return planUnder(this.plan, this.policy);
  }

  /** Sæt spillerens politik (renset) og fortæl panelet. */
  private setPolicy(p: Policy): void {
    const next = sanePolicy(p);
    const wasMoney = this.policy.priority === 'money';
    this.policy = next;
    if (next.priority === 'money' && !wasMoney) this.saveSince = this.deps.state().clockTick;
    if (next.priority !== 'money') this.saveSince = null;
    this.ui.policy?.(next);
  }

  /** "Spar penge" udløber efter et halvt år spiltid. */
  private expireSave(): void {
    if (this.saveSince === null || this.policy.priority !== 'money') return;
    const tick = this.deps.state().clockTick;
    if (tick - this.saveSince < TEMPO.saveTicks) return;
    this.setPolicy({ ...this.policy, priority: 'balanced' });
    this.say('Kassen har fået ro. Jeg bygger igen, sig til hvis jeg skal spare.', 'happy');
  }

  /** Fjern ét af spillerens ønsker (mærket med × i panelet). */
  unset(what: 'region' | 'priority' | 'save' | Policy['avoid'][number]): void {
    const p: Policy = { ...this.policy, avoid: [...this.policy.avoid] };
    if (what === 'region') p.focus_region = 'all';
    else if (what === 'priority') p.priority = 'balanced';
    else if (what === 'save') p.save_for = 'nothing';
    else p.avoid = p.avoid.filter((a) => a !== what);
    this.chatSeq++;
    this.setPolicy(p);
    const ph = this.phase;
    if (ph.k === 'waiting' || ph.k === 'asking' || (ph.k === 'pending' && ph.by !== 'player')) this.drop();
    this.nextThink = this.deps.now() + 600;
  }

  isClaimed(id: string): boolean {
    return (this.claims.get(id) ?? 0) > this.deps.now();
  }

  /** Spillerens instruks: en chip eller fritekst (højst 80 tegn). Virker også uden net. */
  instruct(text: string, chip = false): string {
    const clean = text.replace(/\s+/g, ' ').trim().slice(0, LIMITS.text);
    if (!clean) return '';
    const before = this.policy;
    const local = policyFromText(clean, before);
    // Et spørgsmål får et svar fra det, Volt ser, også uden net, og ændrer intet (IGN runde 1:
    // "Hvorfor byggede du til Nordjyllandsværket?" blev til ordren "Nord").
    const q = chip ? null : questionReply(clean, buildWireState(this.deps.state(), { policy: before, player: this.me }), local.policy !== before);
    // policyFromText giver samme objekt tilbage, når den intet forstod.
    const understood = !q && local.policy !== before;
    if (q) local.reply = q;
    if (understood) this.setPolicy(local.policy);
    // En chip eller ny besked gør et Haiku-svar på en ældre besked forældet.
    const chatReq = ++this.chatSeq;
    // Nyt ønske: glem en annonceret handling eller et spørgsmål, der blev valgt under det gamle,
    // og tænk igen om lidt. Et valg, spilleren selv lige har truffet, får lov at stå.
    const p = this.phase;
    if (p.k === 'waiting' || p.k === 'asking' || (p.k === 'pending' && p.by !== 'player')) this.drop();
    this.nextThink = this.deps.now() + 600;
    if (chip) {
      this.say(local.reply, 'happy');
      return local.reply;
    }
    // Fritekst: svar med det samme (det lokale gæt eller et "øjeblik"), og lad Haiku følge op.
    const ack = understood || q ? local.reply : 'Øjeblik, jeg tygger på den.';
    this.say(ack, 'calm');
    const state = this.deps.state();
    const wire = buildWireState(state, { plan: this.teamPlan, policy: this.policy, recent: this.recent, player: this.me });
    this.deps.transport
      .call({ kind: 'chat', state: wire, text: clean }, deadlineFor('chat'))
      .then((a) => {
        // Kun svaret på den nyeste besked tæller.
        if (chatReq !== this.chatSeq) return;
        if (!isFallback(a) && 'reply' in a) {
          // Et ønske uden for spillet ændrer intet; ellers har Haikus læsning sidste ord.
          if (!a.off_topic) this.setPolicy(a.policy);
          this.say(a.reply, a.off_topic ? 'cheeky' : 'happy');
        } else if (isFallback(a) && a.line) this.sayOnce(a.line, 'calm');
        else if (!understood) this.say(local.reply, 'calm');
      })
      .catch(() => {
        if (chatReq === this.chatSeq && !understood) this.say(local.reply, 'calm');
      });
    return ack;
  }

  private chatSeq = 0;

  // -------------------------------------------------------------------------
  // Haiku: plan og replikker

  private askPlan(): void {
    const now = this.deps.now();
    this.planInFlight = true;
    this.lastPlanAt = now;
    this.planCount++;
    // 45-60 s med en fast, deterministisk spredning.
    this.nextPlanAt = now + TEMPO.planMs + (hash(`plan${this.planCount}`) % TEMPO.planJitterMs);
    const wire = buildWireState(this.deps.state(), { plan: this.teamPlan, policy: this.policy, recent: this.recent, player: this.me });
    this.deps.transport
      .call({ kind: 'plan', state: wire }, deadlineFor('plan'))
      .then((a) => {
        if (!isFallback(a) && 'plan' in a) {
          this.plan = a.plan;
          // Strider planen mod spillerens ønske, siger Volt den ikke højt (spilleren har sidste ord).
          if (a.line && this.teamPlan?.note && this.deps.now() - this.lastLineAt >= TEMPO.lineGapMs / 2) this.say(a.line, 'calm');
        } else if (isFallback(a) && a.line) this.sayOnce(a.line, 'calm');
      })
      .catch(() => undefined)
      .finally(() => {
        this.planInFlight = false;
      });
  }

  /** En hændelse i spillet. Store hændelser fremskynder næste plan. */
  event(ev: LineEvent, about?: string): void {
    const now = this.deps.now();
    if (ev === 'storm' || ev === 'blackout' || ev === 'year') {
      const soon = Math.max(now + 1000, this.lastPlanAt + TEMPO.planMinMs);
      this.nextPlanAt = Math.min(this.nextPlanAt, soon);
    }
    this.line(ev, about);
  }

  private line(ev: LineEvent, about?: string): void {
    const now = this.deps.now();
    const big = ev === 'won' || ev === 'lost' || ev === 'start';
    if (this.lineInFlight || (!big && now - this.lastLineAt < TEMPO.lineGapMs)) return;
    // Ingen jubel, mens en by er mørk eller ved at blive det (playtest runde 3: "Ny ledning. Strømmen
    // har fået en genvej." mens Esbjerg gik i sort).
    if (CHEER.has(ev) && troubled(this.deps.state())) return;
    this.lineInFlight = true;
    this.lastLineAt = now;
    const wire = buildWireState(this.deps.state(), { plan: this.teamPlan, policy: this.policy, player: this.me });
    const req: Parameters<Transport['call']>[0] = { kind: 'line', state: wire, event: ev };
    if (about) req.about = about.replace(/[^\p{L}\p{N} .'-]/gu, '').slice(0, LIMITS.name);
    this.deps.transport
      .call(req, deadlineFor('line'))
      .then((a) => {
        if (!isFallback(a) && 'mood' in a) this.say(a.line, a.mood);
        else if (isFallback(a) && a.line) this.sayOnce(a.line, 'calm');
      })
      .catch(() => undefined)
      .finally(() => {
        this.lineInFlight = false;
      });
  }

  private said = new Set<string>();
  private sayOnce(line: string, mood: Mood): void {
    if (this.said.has(line)) return;
    this.said.add(line);
    this.say(line, mood);
  }

  private say(line: string, mood: Mood): void {
    const l = clipLine(line);
    if (l) this.ui.say?.(l, mood);
  }

  // -------------------------------------------------------------------------
  // Hændelseskort: Volt foreslår, spilleren svarer

  card(threatId: string): void {
    if (this.policy.avoid.includes('cards')) return;
    const state = this.deps.state();
    const cands = cardCandidates(state, threatId);
    if (cands.length === 0) return;
    const fallback = () => {
      const best = cands[0]!;
      this.ui.suggest?.(threatId, (best.cand.opt ?? null) as CardOption | null, best.label, 'rygmarv');
    };
    if (cands.length < 2) {
      fallback();
      return;
    }
    const req = (this.cardReq.get(threatId) ?? 0) + 1;
    this.cardReq.set(threatId, req);
    const t0 = this.deps.now();
    const deadline = deadlineFor('card');
    const wire = buildWireState(state, { plan: this.teamPlan, policy: this.policy, player: this.me });
    let done = false;
    const finish = (a: AgentAnswer) => {
      if (done || this.cardReq.get(threatId) !== req) return;
      done = true;
      if (isFallback(a) || !('choice' in a) || this.deps.now() - t0 > deadline + 50) return fallback();
      const c = cands.find((x) => x.cand.id === a.choice);
      if (!c) return fallback();
      this.ui.suggest?.(threatId, (c.cand.opt ?? null) as CardOption | null, c.label, 'jev');
    };
    this.deps.transport
      .call({ kind: 'card', state: wire, candidates: cands.map((c) => c.wire) }, deadline)
      .then(finish)
      .catch(() => finish({ fallback: true, reason: 'error' }));
  }
}
